<template>
  <HrPageLayout
    title="Documents From Attorney"
    subtitle="Shared legal documents and acknowledgements"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Documents From Attorney' }]"
  >
    <div class="hr-chart-card">
      <div class="hr-chart-body">
        <div class="hr-table-wrapper">
          <table class="hr-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Status</th>
                <th>Shared At</th>
                <th>Acknowledged</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in docs" :key="d.id">
                <td>{{ d.doc_type }}</td>
                <td>{{ d.title }}</td>
                <td>{{ normalizeStatus(d.status) }}</td>
                <td>{{ d.shared_at || '—' }}</td>
                <td>{{ d.acknowledged_at || 'Pending' }}</td>
                <td class="actions-cell">
                  <button
                    class="hr-btn hr-btn-sm hr-btn-outline-primary"
                    :disabled="!hasAttachment(d) || openingId === d.id"
                    @click="openAttachment(d)"
                  >
                    {{ openingId === d.id ? 'Opening…' : 'View Attachment' }}
                  </button>
                  <button
                    class="hr-btn hr-btn-sm hr-btn-outline-secondary"
                    :disabled="!hasAttachment(d) || openingId === d.id"
                    @click="downloadAttachment(d)"
                  >
                    Download
                  </button>
                  <button class="hr-btn hr-btn-sm hr-btn-success" :disabled="!!d.acknowledged_at" @click="ack(d)">
                    {{ d.acknowledged_at ? 'Acknowledged' : 'Acknowledge' }}
                  </button>
                </td>
              </tr>
              <tr v-if="!docs.length"><td colspan="6" class="hr-text-center">No shared documents</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, executeDownload, authUrl, toast } from "@/api";

export default {
  name: "DocumentsFromAttorney",
  components: { HrPageLayout },
  data() {
    return { docs: [], openingId: null };
  },
  mounted() {
    this.loadDocs();
  },
  methods: {
    normalizeStatus(v) {
      const s = String(v || "").trim().toLowerCase();
      if (!s) return "active";
      return s === "share" ? "shared" : s;
    },
    hasAttachment(d) {
      // Allow open by document id; server resolves file_path even if list omits it
      return !!(d && d.id);
    },
    async loadDocs() {
      const fd = new FormData();
      fd.append("function", "getAttorneySharedDocumentsForMe");
      try {
        const res = await execute(fd, authUrl);
        this.docs = res?.data?.data?.documents || [];
      } catch (_) {
        this.docs = [];
        toast.error("Could not load documents");
      }
    },
    async ack(d) {
      const fd = new FormData();
      fd.append("function", "acknowledgeAttorneyDocument");
      fd.append("documentId", String(d.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Acknowledged");
        await this.loadDocs();
      } else {
        toast.error(res?.data?.message || "Acknowledge failed");
      }
    },
    async fetchAttachmentBlob(d) {
      const fd = new FormData();
      fd.append("function", "downloadCountyAttorneyDocument");
      fd.append("id", String(d.id));
      const res = await executeDownload(fd, authUrl);
      const ct = String((res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "");
      const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data || ""], { type: ct || "application/octet-stream" });
      if (ct.indexOf("application/json") !== -1 || (blob.type && blob.type.indexOf("json") !== -1 && blob.size < 4096)) {
        const rawText = await blob.text();
        let msg = "Could not open attachment";
        try {
          const j = JSON.parse(rawText);
          if (j.message) msg = j.message;
        } catch (_) {
          if (rawText) msg = rawText.slice(0, 200);
        }
        throw new Error(msg);
      }
      // sometimes errors come without JSON content-type
      if (blob.size < 800) {
        try {
          const t = await blob.slice(0, 800).text();
          if (t && t.trim().startsWith("{") && t.includes('"success"')) {
            const j = JSON.parse(t);
            if (j.success === false) throw new Error(j.message || "Could not open attachment");
          }
        } catch (e) {
          if (e.message && e.message !== "Could not open attachment") throw e;
          if (e instanceof SyntaxError) {
            /* binary */
          } else if (e.message) {
            throw e;
          }
        }
      }
      return blob;
    },
    filenameFor(d, blob) {
      const base = String(d.title || "attorney-document").replace(/[^\w.\- ]+/g, "_");
      const ct = blob.type || "";
      if (ct.includes("pdf") && !/\.pdf$/i.test(base)) return `${base}.pdf`;
      return base;
    },
    async openAttachment(d) {
      if (!this.hasAttachment(d)) return;
      this.openingId = d.id;
      try {
        const blob = await this.fetchAttachmentBlob(d);
        const url = URL.createObjectURL(blob);
        const opened = window.open(url, "_blank");
        if (!opened) {
          // popup blocked — force download instead
          const a = document.createElement("a");
          a.href = url;
          a.download = this.filenameFor(d, blob);
          document.body.appendChild(a);
          a.click();
          a.remove();
          toast.info("Pop-up blocked — attachment downloaded instead");
        }
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (e) {
        toast.error(e?.message || "Could not open attachment");
      } finally {
        this.openingId = null;
      }
    },
    async downloadAttachment(d) {
      if (!this.hasAttachment(d)) return;
      this.openingId = d.id;
      try {
        const blob = await this.fetchAttachmentBlob(d);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.filenameFor(d, blob);
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        toast.error(e?.message || "Download failed");
      } finally {
        this.openingId = null;
      }
    }
  }
};
</script>

<style scoped>
.actions-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
</style>
